import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Avatar } from '@mui/material';
import { MdPerson } from 'react-icons/md';
import { useUserStore } from '../../stores/userStore';
import { useNotificationStore } from '../../stores/notificationStore';
import defaultUserThumbnail from '../../assets/default_user_thumbnail.svg';
import theme from '../../theme';
import CustomMenu from './CustomMenu';
import { REFERRER_TYPE } from '../../constants/gaEnums';

/**
 * 헤더에 표시되는 프로필 버튼 컴포넌트
 * - 로그인 시: 프로필 사진 표시, 클릭 시 메뉴
 * - 비로그인 시: 기본 아이콘 표시, 클릭 시 로그인 페이지로 이동
 */
const HeaderProfileButton = ({ variant = 'desktop', onClick }) => {
  const user = useUserStore((state) => state.user);
  const unreadCount = useNotificationStore((state) => state.unreadCount);
  const isLoggedIn = user && Object.keys(user).length > 0;
  const hasUnread = unreadCount > 0;
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  // variant에 따른 크기 설정
  const sizes = {
    desktop: {
      box: 44,
      avatar: 32,
      icon: 24,
      dot: 8,
    },
    tablet: {
      box: 32,
      avatar: 24,
      icon: 24,
      dot: 6,
    },
    mobile: {
      box: 32,
      avatar: 24,
      icon: 24,
      dot: 6,
    },
  };

  const currentSizes = sizes[variant] || sizes.desktop;

  const handleClick = (event) => {
    if (isLoggedIn) {
      setAnchorEl(event.currentTarget);
    } else {
      navigate('/login');
    }
    onClick?.();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const menuItems = [
    {
      text: '마이페이지',
      onClick: () => {
        handleClose();
        navigate('/mypage', { state: { referrerType: REFERRER_TYPE.MENU } });
        window.scrollTo(0, 0);
      },
    },
    {
      text: '내 알림 목록',
      badge: hasUnread,
      onClick: () => {
        handleClose();
        navigate('/notifications', { state: { referrerType: REFERRER_TYPE.MENU } });
        window.scrollTo(0, 0);
      },
    },
  ];

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          width: currentSizes.box,
          height: currentSizes.box,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          position: 'relative',
        }}
        title={isLoggedIn ? '프로필 메뉴' : '로그인'}
      >
        {isLoggedIn ? (
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={user.profileImageUrl || defaultUserThumbnail}
              sx={{
                width: currentSizes.avatar,
                height: currentSizes.avatar,
                border: `1px solid ${theme.palette.grey.line}`,
              }}
            />
            {hasUnread && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: currentSizes.avatar - currentSizes.dot,
                  width: currentSizes.dot,
                  height: currentSizes.dot,
                  borderRadius: `${currentSizes.dot / 2}px`,
                  backgroundColor: theme.palette.secondary.main,
                }}
              />
            )}
          </Box>
        ) : (
          <MdPerson size={currentSizes.icon} color={theme.palette.black[3]} />
        )}
      </Box>
      <CustomMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        items={menuItems}
      />
    </>
  );
};

export default HeaderProfileButton;
